/**
 * Display vocabulary for the revenue analytics page.
 *
 * Labels, month names, bucket names and the presentation of a backend-computed
 * change. Deliberately NOT a place where money is derived: every function here
 * either formats a value it was handed or maps an enum to German.
 *
 * `fmtMoney` is not re-implemented — it is imported from the Bexio format module
 * where it already lives and is already tested (`CHF 1'500.00`). A second Swiss
 * money formatter is how two screens end up disagreeing about a thousands
 * separator.
 */
import {
  fmtInvoiceMoney,
  fmtMoney,
} from "../../../components/admin/bexio/bexioFormat";

// `fmtMoney` for figures the contract declares CHF; `fmtInvoiceMoney` for the
// per-invoice amounts in the attribution findings, whose currency is nullable.
export { fmtInvoiceMoney, fmtMoney };

export const MONTHS_DE = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];

const MONTHS_DE_SHORT = [
  "Jan",
  "Feb",
  "Mrz",
  "Apr",
  "Mai",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Okt",
  "Nov",
  "Dez",
];

/** "August 2026" from a `{year, month}` pair. Never parsed from a date string. */
export function periodLabel(period) {
  if (!period || period.month == null || period.year == null) return "—";
  return `${MONTHS_DE[period.month - 1] || period.month} ${period.year}`;
}

/**
 * A month WINDOW as one label: "September 2026", "April – September 2026",
 * "Oktober 2025 – März 2026".
 *
 * P2. Added beside `periodLabel` rather than as a private helper in the
 * Performance tab, so the German month names stay in ONE place — the moment a
 * second component owns its own month array, the two start drifting (the period
 * picker already carries a duplicate, which is why this one is not a third).
 *
 * Takes the two structured month refs the Performance contract publishes
 * (`period.start` / `period.end`), NOT a formatted string: the window is a fact
 * about the response, and re-parsing prose to recover it would be a parsing
 * hazard the backend deliberately removed.
 *
 * Deterministic: `MONTHS_DE`, never `toLocaleDateString` — the label must read
 * the same for a viewer in Zurich and one in Sydney.
 */
export function periodRangeLabel(start, end) {
  const from = periodLabel(start);
  const to = periodLabel(end);
  if (from === "—" && to === "—") return "—";
  // One bound missing is not a range; show whichever is real rather than an
  // "— – September 2026" that looks like data loss.
  if (from === "—") return to;
  if (to === "—") return from;
  if (start.year === end.year && start.month === end.month) return to;
  if (start.year === end.year) {
    return `${MONTHS_DE[start.month - 1] || start.month} – ${to}`;
  }
  return `${from} – ${to}`;
}

/** "Aug 2026" for a chart axis. */
export function shortPeriodLabel(point) {
  if (!point || point.month == null) return "";
  return `${MONTHS_DE_SHORT[point.month - 1] || point.month} ${point.year}`;
}

/**
 * A Decimal-as-JSON-string turned into a Number FOR CHART GEOMETRY ONLY.
 *
 * Pydantic serializes Decimal losslessly as a string, which is right for money
 * and useless for pixel maths — a chart library needs a Number to place a point.
 * So the conversion happens at the rendering boundary and nowhere else: nothing
 * is added, subtracted or compared afterwards, and every figure the user READS
 * goes through `fmtMoney` with the original string.
 */
export function toChartNumber(value) {
  if (value == null || value === "") return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Is this an explicit, present zero — as opposed to an absent figure?
 *
 * A DISPLAY PREDICATE, not arithmetic. The backend returns `"0.00"` for a rent
 * month whose actuals nothing proves, and that zero is a statement: "nothing is
 * attributed here". An absent value (`null`, `""`) is a different thing entirely
 * and `fmtMoney` renders it as a dash — it must NOT collect the same
 * explanation, because "we have no figure" and "the figure is zero, and here is
 * why" are not the same claim.
 *
 * Nothing is added, subtracted or compared against another money value; the
 * comparison is against the literal 0 for the sole purpose of deciding whether a
 * sentence appears on screen.
 */
export function isExplicitZero(value) {
  if (value == null || value === "") return false;
  const n = Number(value);
  return Number.isFinite(n) && n === 0;
}

/** Axis tick: 21180 -> "21K". Display shorthand, never a stored figure. */
export function compactAxisTick(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  const abs = Math.abs(n);
  if (abs >= 1000) return `${Math.round(n / 1000)}K`;
  return String(Math.round(n));
}

/**
 * How a backend-computed change is shown.
 *
 * Reads `absolute_change`, `percent_change` and `percent_change_available` and
 * does NO arithmetic of its own — there is deliberately no
 * `(selected - comparison) / comparison` anywhere in this feature. When the
 * comparison figure was zero there is no defined percentage, and the backend
 * says so; this returns a neutral dash rather than inventing "+100 %", "∞" or
 * the word "neu".
 *
 * `direction` drives an ARROW as well as the colour, so the sign of a financial
 * movement is never carried by hue alone.
 *
 * `absoluteText` is the backend's `absolute_change` FORMATTED, never rebuilt:
 * `fmtMoney` receives the contract string untouched and keeps its own minus sign
 * (`CHF -700.00`), while a positive change gains the `+` the same way
 * `percentText` does. There is no `Math.abs`, no string surgery on the amount and
 * no `selected - comparison` — the figure on screen is the figure the server
 * computed.
 *
 * @returns {{direction: "up"|"down"|"flat", percentText: string,
 *            hasPercent: boolean, absoluteText: string, srText: string}}
 */
export function changeDisplay(change) {
  const absolute = change?.absolute_change;
  const abs = Number(absolute ?? 0);
  const direction = abs > 0 ? "up" : abs < 0 ? "down" : "flat";

  // Sign convention mirrored from `percentText` below: a rise is marked, a fall
  // carries the minus the formatter already prints.
  const absoluteText = `${direction === "up" ? "+" : ""}${fmtMoney(absolute)}`;

  const hasPercent =
    change?.percent_change_available === true && change?.percent_change != null;

  if (!hasPercent) {
    return {
      direction,
      hasPercent: false,
      percentText: "—",
      absoluteText,
      srText:
        "Keine Prozentangabe verfügbar, der Vergleichswert ist null.",
    };
  }

  const pct = Number(change.percent_change);
  const sign = pct > 0 ? "+" : "";
  const percentText = `${sign}${pct.toLocaleString("de-CH", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} %`;

  return {
    direction,
    hasPercent: true,
    percentText,
    absoluteText,
    srText:
      direction === "up"
        ? `gestiegen um ${percentText}`
        : direction === "down"
        ? `gesunken um ${percentText}`
        : `unverändert, ${percentText}`,
  };
}

/**
 * Aging bucket keys -> German.
 *
 * All SEVEN backend buckets appear. `due_today` is not folded into
 * "1–30 Tage" (an invoice due today has an age of zero days, so that label
 * would be false), and `no_due_date` is not folded into "Nicht fällig" — those
 * are open receivables whose age is UNKNOWN, which is a different statement.
 */
export const AGING_BUCKET_ORDER = [
  "not_due",
  "due_today",
  "overdue_1_30",
  "overdue_31_60",
  "overdue_61_90",
  "overdue_90_plus",
  "no_due_date",
];

export const AGING_BUCKET_LABEL = {
  not_due: "Nicht fällig",
  due_today: "Heute fällig",
  overdue_1_30: "1–30 Tage",
  overdue_31_60: "31–60 Tage",
  overdue_61_90: "61–90 Tage",
  overdue_90_plus: "> 90 Tage",
  no_due_date: "Ohne Fälligkeitsdatum",
};

/**
 * Series colours.
 *
 * Written as literal hex rather than Tailwind classes because recharts needs a
 * paint value, not a class. Chosen to read on both the light and the dark card
 * background; the axes and gridlines use `currentColor` instead so they follow
 * the theme automatically.
 */
export const SERIES_COLOR = {
  expected: "#2563eb",
  invoiced: "#60a5fa",
  paid: "#16a34a",
  open: "#f59e0b",
  oneTime: "#8b5cf6",
  recurringOther: "#a855f7",
  cash: "#0ea5e9",
};

/** A `unit_label` that may be missing. Never invents a name. */
export function unitDisplayLabel(unit) {
  if (unit?.unit_label) return unit.unit_label;
  const id = String(unit?.unit_id ?? "");
  return id ? `Einheit ${id.slice(0, 8)}` : "Unbekannte Einheit";
}
